import React, { useState } from "react";
import { Text, TextInput, TouchableOpacity, Platform } from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ParentStackParamList } from "../../navigation/types";
import { authApi } from "../../api/auth";
import { useAuthStore } from "../../state/authStore";
import AuthShell, { AuthField, authStyles } from "../../components/AuthShell";
import { colors } from "../../theme";

type Props = NativeStackScreenProps<ParentStackParamList, "ForgotPassword">;

// Fully in-app password reset — no link, no browser. Step 1 emails a 6-digit
// code; step 2 takes that code + a new password and signs the parent in.
export default function ForgotPasswordScreen({ navigation }: Props) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const { setTokens } = useAuthStore();

  const goHome = () =>
    (navigation as any).replace(
      Platform.OS === "web" ? "WebShell" : "ProfileSelect",
    );

  const sendCode = async () => {
    setError(null);
    if (!email.trim()) {
      setError("Enter the email you signed up with.");
      return;
    }
    setLoading(true);
    try {
      await authApi.requestPasswordReset(email.trim());
    } catch {
      // Never leak account existence — advance regardless.
    } finally {
      setLoading(false);
    }
    setStep("code");
  };

  const resetPassword = async () => {
    setError(null);
    if (code.trim().length !== 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    if (password.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const tokens = await authApi.resetPassword(
        email.trim(),
        code.trim(),
        password,
      );
      setTokens(tokens.accessToken, tokens.refreshToken, tokens.parentId);
      goHome();
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : "That code is invalid or has expired. Request a new one.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      mode="signin"
      eyebrow="Account help"
      title="Reset your password"
      subtitle={
        step === "email"
          ? "Enter your email and we'll send you a 6-digit code."
          : `Enter the code we emailed to ${email.trim()} and choose a new password.`
      }
      onSwitchMode={() => {}}
      footer={
        <TouchableOpacity onPress={() => navigation.navigate("Login")}>
          <Text style={authStyles.link}>← Back to sign in</Text>
        </TouchableOpacity>
      }
    >
      {step === "email" ? (
        <>
          <AuthField label="Email">
            <TextInput
              style={[
                authStyles.input,
                focused === "email" && authStyles.inputFocused,
              ]}
              placeholder="you@home.com"
              placeholderTextColor={colors.ink3}
              autoCapitalize="none"
              keyboardType="email-address"
              value={email}
              onChangeText={setEmail}
              onFocus={() => setFocused("email")}
              onBlur={() => setFocused(null)}
              onSubmitEditing={sendCode}
            />
          </AuthField>

          {error ? <Text style={authStyles.error}>{error}</Text> : null}

          <TouchableOpacity
            style={[authStyles.submit, loading && authStyles.submitDisabled]}
            onPress={sendCode}
            disabled={loading}
            activeOpacity={0.85}
          >
            <Text style={authStyles.submitText}>
              {loading ? "Sending…" : "Send code"}
            </Text>
          </TouchableOpacity>
        </>
      ) : (
        <>
          <AuthField label="6-digit code">
            <TextInput
              style={[
                authStyles.input,
                focused === "code" && authStyles.inputFocused,
                { letterSpacing: 6, fontWeight: "800" },
              ]}
              placeholder="000000"
              placeholderTextColor={colors.ink3}
              keyboardType="number-pad"
              maxLength={6}
              value={code}
              onChangeText={setCode}
              onFocus={() => setFocused("code")}
              onBlur={() => setFocused(null)}
            />
          </AuthField>

          <AuthField label="New password">
            <TextInput
              style={[
                authStyles.input,
                focused === "password" && authStyles.inputFocused,
              ]}
              placeholder="At least 8 characters"
              placeholderTextColor={colors.ink3}
              secureTextEntry
              value={password}
              onChangeText={setPassword}
              onFocus={() => setFocused("password")}
              onBlur={() => setFocused(null)}
              onSubmitEditing={resetPassword}
            />
          </AuthField>

          {error ? <Text style={authStyles.error}>{error}</Text> : null}

          <TouchableOpacity
            style={[authStyles.submit, loading && authStyles.submitDisabled]}
            onPress={resetPassword}
            disabled={loading}
            activeOpacity={0.85}
          >
            <Text style={authStyles.submitText}>
              {loading ? "Saving…" : "Reset password"}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={{ alignSelf: "center", marginTop: 16 }}
            onPress={sendCode}
            disabled={loading}
          >
            <Text style={authStyles.link}>Resend code</Text>
          </TouchableOpacity>
        </>
      )}
    </AuthShell>
  );
}
